import { atom, useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import type { RecoilState, SetterOrUpdater } from 'recoil';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import type { ToolID } from 'hooks/usePreferences/allTools';

const DEFAULT_TOOL: ToolID = 'REGULAR_SCROLL';

export const selectedToolState: RecoilState<ToolID> = atom({
  key: 'selectedTool',
  default: DEFAULT_TOOL,
  // The persister must run first so the synchronizer can seed from the restored tool; otherwise a
  // reloaded window never shares it and ignores peers' changes that match its stale cache (VX-970).
  effects: [
    localStoragePersisterEffect(),
    broadcastChannelSynchronizerEffect<ToolID>({
      seedRestoredValue: { defaultValue: DEFAULT_TOOL },
    }),
  ],
});

export const useSelectedTool = (): [ToolID, SetterOrUpdater<ToolID>] => {
  return useRecoilState(selectedToolState);
};

export const useSelectedToolValue = (): ToolID => {
  return useRecoilValue(selectedToolState);
};

export const useSetSelectedTool = (): SetterOrUpdater<ToolID> => {
  return useSetRecoilState(selectedToolState);
};

export const withSelectedTool = <P,>(
  Component: React.ComponentType<P>
): React.ComponentType<
  P & {
    selectedTool: ToolID;
    setSelectedTool: SetterOrUpdater<ToolID>;
  }
> =>
  function WithSelectedTool(props) {
    const [selectedTool, setSelectedTool] = useSelectedTool();
    return <Component {...props} selectedTool={selectedTool} setSelectedTool={setSelectedTool} />;
  };
